if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        /* ModalDialog closes a `.media-modal` on any mouse pointerup that is
           not inside `deferred-media` or `product-model`. That listener is on
           the modal itself, so an arrow or a thumbnail click bubbles up to it
           and dismisses the lightbox instead of moving it.

           Stopping the event at the control is what keeps both behaviours: the
           backdrop still closes on click, the controls no longer do. Capture
           phase, so this runs before the listener the base class attached. */
        this.addEventListener(
          'pointerup',
          (event) => {
            if (event.target.closest('[data-modal-control]')) event.stopPropagation();
          },
          true
        );

        this.addEventListener('click', (event) => {
          const control = event.target.closest('[data-modal-control]');
          if (!control) return;

          const step = control.dataset.modalStep;
          if (step) {
            this.step(parseInt(step, 10));
            return;
          }

          const target = control.dataset.modalTarget;
          if (target) this.setActive(target);
        });

        /* Arrow keys, because a gallery that can only be driven by the mouse
           is a gallery half the visitors cannot drive. */
        this.addEventListener('keydown', (event) => {
          if (event.code === 'ArrowRight') this.step(1);
          if (event.code === 'ArrowLeft') this.step(-1);
        });

        this.setUpSwipe();
      }

      /* Swiping, for phones and tablets. The picture follows the finger
         sideways; let go past a short distance (or with a quick flick) and the
         next or previous picture slides in, otherwise it springs back. At the
         first and last picture it gives a little and springs back, matching the
         arrows, which stop at the ends too.

         `touch-action: pan-y pinch-zoom` hands sideways movement to this code
         while the browser keeps vertical scrolling and pinch-to-zoom. A
         two-finger touch, or a page that is already pinched in, is left to the
         browser so zooming into a photo never flips to the next one. */
      setUpSwipe() {
        const area = this.querySelector('.product-media-modal__content');
        if (!area) return;
        area.style.touchAction = 'pan-y pinch-zoom';

        let startX = 0;
        let startY = 0;
        let startTime = 0;
        let tracking = false;
        let horizontal = null;
        let dragged = null;
        let dx = 0;

        const zoomed = () => window.visualViewport && window.visualViewport.scale > 1.01;

        const release = (element, animate) => {
          if (!element) return;
          element.style.transition = animate ? 'transform 200ms ease' : '';
          element.style.transform = '';
          if (animate) {
            setTimeout(() => {
              element.style.transition = '';
            }, 220);
          }
        };

        area.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1 || zoomed()) {
              tracking = false;
              release(dragged, true);
              dragged = null;
              return;
            }
            const touch = event.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            startTime = Date.now();
            tracking = true;
            horizontal = null;
            dx = 0;
            dragged = this.items.find((item) => item.classList.contains('active')) || null;
            if (dragged) dragged.style.transition = 'none';
          },
          { passive: true }
        );

        area.addEventListener(
          'touchmove',
          (event) => {
            if (!tracking || event.touches.length !== 1) return;
            const touch = event.touches[0];
            dx = touch.clientX - startX;
            const dy = touch.clientY - startY;

            if (horizontal === null) {
              if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
              horizontal = Math.abs(dx) > Math.abs(dy);
            }
            if (!horizontal || !dragged) return;

            // Resistance at the ends, where there is nothing to swipe to.
            const items = this.items;
            const index = items.indexOf(dragged);
            const atEnd = (dx > 0 && index <= 0) || (dx < 0 && index >= items.length - 1);
            dragged.style.transform = `translateX(${atEnd ? dx * 0.25 : dx}px)`;
          },
          { passive: true }
        );

        const finish = () => {
          if (!tracking) return;
          tracking = false;
          const element = dragged;
          dragged = null;
          if (!horizontal || !element) {
            release(element, false);
            return;
          }

          const width = area.clientWidth || window.innerWidth;
          const fast = Math.abs(dx) > 30 && Date.now() - startTime < 250;
          const far = Math.abs(dx) > Math.min(80, width * 0.2);
          const direction = dx < 0 ? 1 : -1;
          const items = this.items;
          const index = items.indexOf(element);
          const target = items[index + direction];

          if (!(fast || far) || !target) {
            release(element, true);
            return;
          }

          release(element, false);
          this.step(direction);

          // The new picture slides in from the side the finger came from.
          target.style.transition = 'none';
          target.style.transform = `translateX(${direction * Math.min(width * 0.35, 140)}px)`;
          target.style.opacity = '0.4';
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              target.style.transition = 'transform 220ms ease, opacity 220ms ease';
              target.style.transform = '';
              target.style.opacity = '';
              setTimeout(() => {
                target.style.transition = '';
              }, 240);
            });
          });
        };

        area.addEventListener('touchend', finish);
        area.addEventListener('touchcancel', finish);
      }

      /* Only the pictures in scope. Everything the product has is rendered into
         the lightbox, and `syncToGallery` marks the ones the shopper is not
         looking at — so this is the list the arrows walk and the list the
         counter would count, not the full set. */
      get items() {
        return Array.from(this.querySelectorAll('.product-media-modal__content [data-media-id]')).filter(
          (item) => !item.hasAttribute('data-modal-excluded')
        );
      }

      hide() {
        super.hide();
      }

      show(opener) {
        /* Before the base class shows anything, so the first paint is already
           the right set — narrowing it afterwards would flash the images that
           are about to be removed. */
        this.syncToGallery();
        super.show(opener);
        this.showActiveMedia();
      }

      /* ---- Which pictures the lightbox is showing ---------------------------

         The lightbox is rendered once, from the variant Liquid saw at page
         load. The gallery on the page is not: Dawn replaces it when the variant
         changes, and a variant-image app filters it in the browser without the
         theme being told. Deciding the set server-side therefore decides it
         from stale information, and the pop-up ends up showing every shot the
         product has while the gallery beside it shows one colour.

         So the gallery is asked directly, at the moment the lightbox opens.
         Whatever media the gallery is currently listing is what the lightbox
         holds; everything else is marked out of scope. */
      syncToGallery() {
        if (this.dataset.lightboxMode !== 'live') return;

        const sectionId = this.dataset.sectionId;
        const gallery = sectionId ? document.getElementById(`MediaGallery-${sectionId}`) : null;
        const inScope = gallery ? this.galleryMediaIds(gallery, sectionId) : null;

        /* No gallery found, or nothing in it matched — an app that replaced the
           gallery wholesale, say. Showing every picture is wrong but harmless;
           showing none is a broken lightbox, so the filter stands down. */
        const filtering = inScope !== null && inScope.size > 0;

        this.querySelectorAll('.product-media-modal__content [data-media-id]').forEach((item) => {
          const keep = !filtering || inScope.has(item.dataset.mediaId);
          item.toggleAttribute('data-modal-excluded', !keep);
          /* Excluded items simply never get `.active`, and `.active` is the only
             thing the carousel CSS reads — so there is nothing further to hide
             here. The rail is a separate list and does need hiding. */
        });

        this.querySelectorAll('[data-modal-rail-item]').forEach((railItem) => {
          const keep = !filtering || inScope.has(railItem.dataset.modalRailItem);
          railItem.toggleAttribute('data-modal-excluded', !keep);
        });

        /* One picture left: the arrows have nowhere to go and a rail of one
           thumbnail is just the picture again, smaller. Both are written into
           the markup whenever the product has more than one image, so they are
           taken away here rather than there. */
        const alone = this.items.length < 2;
        const rail = this.querySelector('.product-media-modal__rail');
        if (rail) rail.toggleAttribute('data-modal-excluded', alone);
        this.querySelectorAll('.product-media-modal__nav').forEach((nav) => {
          nav.toggleAttribute('data-modal-excluded', alone);
        });
      }

      /* The media ids the gallery is listing, with the section prefix its slides
         carry stripped off — the lightbox keys on the bare media id.

         Membership is DOM presence, not computed visibility: the thumbnail
         layouts hide every slide but the active one in CSS, and those pictures
         are still part of the gallery. What is checked instead are the ways a
         script hides something at runtime, which is how a variant-image app
         takes a photo out of a gallery it did not render. */
      galleryMediaIds(gallery, sectionId) {
        const prefix = `${sectionId}-`;
        const ids = new Set();

        gallery.querySelectorAll('.product__media-item[data-media-id]').forEach((slide) => {
          const raw = slide.dataset.mediaId || '';
          if (!raw.startsWith(prefix)) return;
          if (slide.hidden || slide.getAttribute('aria-hidden') === 'true') return;
          if (slide.style.display === 'none' || slide.style.visibility === 'hidden') return;
          ids.add(raw.slice(prefix.length));
        });

        return ids;
      }

      /* Moves by position in the list rather than by id, and stops at the ends
         rather than wrapping: with a thumbnail rail on screen, wrapping from
         the last image back to the first reads as a glitch because the rail
         jumps the whole way back with it. */
      step(direction) {
        const items = this.items;
        if (items.length === 0) return;

        const current = items.findIndex((item) => item.classList.contains('active'));
        const next = Math.min(items.length - 1, Math.max(0, current + direction));
        if (next === current) return;

        this.setActive(items[next].dataset.mediaId);
      }

      setActive(mediaId) {
        const items = this.items;
        const activeMedia = items.find((item) => item.dataset.mediaId === String(mediaId));
        if (!activeMedia) return;

        /* Cleared across everything, not just the items in scope: an image that
           has just gone out of scope would otherwise keep the class and stay on
           screen beside the one that is meant to be there. */
        this.querySelectorAll('.product-media-modal__content [data-media-id]').forEach((item) => {
          item.classList.toggle('active', item === activeMedia);
        });

        this.querySelectorAll('[data-modal-target]').forEach((button) => {
          const isCurrent = button.dataset.modalTarget === String(mediaId);
          button.classList.toggle('is-current', isCurrent);
          /* aria-current, not just a class: the rail is a list of controls and
             a screen reader needs to know which one is showing. */
          if (isCurrent) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });

        this.updateArrows(items.indexOf(activeMedia), items.length);

        /* Whatever was playing in the lightbox stops when the shopper moves
           on. Then a video that has just become the current picture starts by
           itself — before, only YouTube did, and a Shopify-hosted video sat as
           a poster that needed a second click. */
        this.querySelectorAll('.product-media-modal__content video').forEach((video) => video.pause());
        if (activeMedia.nodeName === 'DEFERRED-MEDIA' && activeMedia.querySelector('template')) {
          activeMedia.loadContent(false);
          const video = activeMedia.querySelector(':scope > video');
          if (video && activeMedia.getAttribute('loaded')) video.play().catch(() => {});
        }

        this.preloadNeighbours(items, items.indexOf(activeMedia));
      }

      /* The pictures either side of the current one are fetched now, while the
         shopper is looking, so an arrow press shows the next one at once rather
         than an empty frame while it downloads. Everything further away stays
         lazy, so opening the lightbox no longer costs every image up front. */
      preloadNeighbours(items, index) {
        [items[index - 1], items[index + 1]].forEach((item) => {
          if (item && item.nodeName === 'IMG' && item.loading === 'lazy') item.loading = 'eager';
        });
      }

      /* Disabled rather than hidden at the ends: a control that disappears
         moves everything beside it, and the arrows sit against the image. */
      updateArrows(index, total) {
        const prev = this.querySelector('[data-modal-step="-1"]');
        const next = this.querySelector('[data-modal-step="1"]');
        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= total - 1;
      }

      showActiveMedia() {
        const items = this.items;
        const openedId = this.openedBy?.getAttribute('data-media-id');

        /* The opener's picture, when it is one of the ones in scope — a
           thumbnail click should land on the thumbnail that was clicked.
           Otherwise the first in scope: that is the "more images" button, which
           carries no media id, and it is also the safe landing when the gallery
           has moved on from whatever opened the lightbox last. */
        const opened = openedId && items.some((item) => item.dataset.mediaId === String(openedId));
        this.setActive(opened ? openedId : items[0]?.dataset.mediaId);
      }
    }
  );
}
